import { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import moment from "moment";
import { Container } from "../../components/Container";
import { Title } from "../../components/Title";
import { TextInput } from "../../components/TextInput";
import { SubmitButton } from "../../components/Button";
import { Loader } from "../../components/Loader";
import { SelectBox } from "../../components/SelectBox";
import UserUtils from "../../util/UserUtils";
import useStatus, { orderStatusColor } from "../../hooks/useStatus";
import { fetchOrdersWithFilters, exportOrders } from "../../api/AWSAPI";
import { UserRole, hasRole } from "../../util/roles";

function OrdersPage() {
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState("");
  const [page, setPage] = useState(1);
  const size = 30;
  const [pageLoaded, setPageLoaded] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [nextToken, setNextToken] = useState(null);
  const [orderStart, setOrderStart] = useState(null);
  const [orderEnd, setOrderEnd] = useState(null);
  const [status, setStatus] = useState(0);
  const [fromDate, setFromDate] = useState(
    moment().add(-30, "day").format("YYYY-MM-DD")
  );
  const [toDate, setToDate] = useState(moment().format("YYYY-MM-DD"));
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState("");
  const [userRoles, setUserRoles] = useState([]);

  const { statuses } = useStatus();

  const statusOptions = useMemo(() => {
    return [
      { value: 0, label: "All Statuses" },
      ...Object.entries(statuses).sort((a, b) => a[1].orderStatusSequence - b[1].orderStatusSequence).map(([key, status]) => ({
        value: Number.parseInt(key, 10),
        label: status.orderStatusText,
      })),
    ];
  }, [statuses]);

  const loadUser = () => {
    UserUtils.getUserData((data) => {
      if (data) {
        setUserRoles(data.roles || []);
      } else {
        navigate("/");
      }
    });
  };

  useEffect(() => {
    fetchOrders({ keyword: "", fromDate, toDate, status, size, nextToken: null });
    loadUser();
  }, []);

  useEffect(() => {
    setError("");
  }, [keyword, fromDate, toDate, status]);

  const filterOrders = () => {
    reset();
    fetchOrders({ keyword, fromDate, toDate, status, size, nextToken: null });
  };

  const fetchOrders = (filters) => {
    const {
      keyword,
      fromDate,
      toDate,
      status,
      size,
      nextToken,
      orderStart,
      orderEnd,
      orderAt,
      previous
    } = filters;
    setIsLoading(true);
    fetchOrdersWithFilters(
      {
        keyword,
        fromDate,
        toDate,
        status,
        size,
        nextToken,
        orderStart,
        orderEnd,
        orderAt,
        previous
      },
      (response) => {
        if (response && response.success) {
          const resNextToken = response.pagination?.nextToken
          const resOrderStart = response.pagination?.orderStart
          const resOrderEnd = response.pagination?.orderEnd
          if (resOrderStart && resOrderEnd) {
            setHasNextPage(response.pagination?.hasMore || false);
            setNextToken(null);
            setOrderStart(resOrderStart);
            setOrderEnd(resOrderEnd);
            setOrders(response.results || []);
            if (previous === "true") {
              setPageLoaded((prevPageLoaded) => prevPageLoaded - 1);
            } else {
              setPageLoaded((prevPageLoaded) => prevPageLoaded + 1);
            }

          } else {
            setOrders((prevOrders) =>
              [...prevOrders, ...response.results].sort((a, b) => b.orderNumber.replaceAll("DPI", "") - a.orderNumber.replaceAll("DPI", ""))
            );
            setPageLoaded((prevPageLoaded) => prevPageLoaded + 1);
            setHasNextPage(response.pagination?.hasMore || false);
            setNextToken(resNextToken || null);
            setOrderStart(null);
            setOrderEnd(null);
          }
        } else {
          reset();
        }

        setIsLoading(false);
      }
    );
  };

  const reset = () => {
    setPage(1);
    setHasNextPage(false);
    setNextToken(null);
    setOrders([]);
    setPageLoaded(0);
    setOrderStart(null);
    setOrderEnd(null);
    setError('');
  };

  const exportData = () => {
    setError('');
    setIsExporting(true);
    exportOrders({ fromDate, toDate, status, keyword }, (csv) => {
      try{
        const url = window.URL.createObjectURL(csv);
        const a = document.createElement('a');
        a.href = url;
        a.download = `orderExport-${moment().format("YYYYMMDDHHmmss")}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        setError('Error exporting data. Please try again later.');
      }
      finally {
        setIsExporting(false);
      }
    });
  };

  const prevPage = () => {
    if (page === 1) return;
    if (orderStart && orderEnd && orders.length > 0) {
      fetchOrders({ keyword, fromDate, toDate, status, size, nextToken, orderStart, orderEnd, orderAt: orders.at(0).orderNumber, previous: "true" });
      setPage(prevPage => prevPage - 1);
    } else {
      setPage(prevPage => prevPage - 1);
    }
  };

  const nextPage = () => {
    if (hasNextPage && orderStart && orderEnd) {
      fetchOrders({ keyword, fromDate, toDate, status, size, nextToken, orderStart, orderEnd, orderAt: orders.at(-1).orderNumber, previous: "false" });
      setPage(prevPage => prevPage + 1);
    } else if (nextToken || pageLoaded) {
      if (page < pageLoaded) {
        setPage(prevPage => prevPage + 1);
      } else if (page === pageLoaded && hasNextPage) {
        fetchOrders({ keyword, fromDate, toDate, status, size, nextToken });
        setPage(prevPage => prevPage + 1);
      }
    }
  };

  const getPaginatedOrders = () => (
    (orderStart && orderEnd) ? orders : orders.slice(((page - 1) * size), ((page * size)))
  );

  const canCreateOrder = hasRole(userRoles, UserRole.Admin, UserRole.NagcoMaintainer);

  return (
    <div className="container-fluid">
      {error && (
            <div className="alert alert-danger" style={{ textAlign: 'center' }} role="alert">
                {error}
            </div>
        )}
      <div className="row row-eq-height">
        <div className="col-sm-12 content">
          <Title
            title="Cash Sales Orders"
            button={canCreateOrder ? "Add new" : ""}
            link={canCreateOrder ? "/orders/edit/new" : ""}
          />
          <Container>
            <div className="row">
              <div className="col-sm-3">
                <TextInput
                  name="keyword"
                  text="Search"
                  label="DPI Order"
                  value={keyword}
                  onChange={(name, value) => setKeyword(value)}
                />
              </div>
              <div className="col-sm-3">
                <TextInput
                  type="date"
                  name="fromDate"
                  placeholder="From"
                  label="Period From"
                  value={fromDate}
                  onChange={(name, value) => setFromDate(value)}
                />
              </div>
              <div className="col-sm-3">
                <TextInput
                  type="date"
                  name="toDate"
                  placeholder="To"
                  label="Period To"
                  value={toDate}
                  onChange={(name, value) => setToDate(value)}
                />
              </div>
              <div className="col-sm-3">
                <SelectBox
                  name="status"
                  label="Status"
                  value={status}
                  maxLength={45}
                  options={statusOptions}
                  onChange={(name, value) => setStatus(value)}
                />
              </div>
            </div>
            <div className="row">
              <div className="col-sm-2 offset-sm-4">
                <div className="form-group">
                  <label>&nbsp;</label>
                  <div className="input-group">
                    <SubmitButton
                      text="Filter"
                      className="btn btn-full"
                      full={true}
                      onClick={filterOrders}
                    />
                  </div>
                </div>
              </div>
              <div className="col-sm-2">
                <div className="form-group">
                  <label>&nbsp;</label>
                  <div className="input-group">
                    <SubmitButton
                      text={isExporting ? "Exporting..." : "Export"}
                      className="btn btn-full btn-secondary"
                      full={true}
                      onClick={exportData}
                      disabled={isExporting}
                    />
                  </div>
                </div>
              </div>
            </div>

            <hr />

            {isLoading ? (
              <Loader />
            ) : (
              <div>
                <table>
                  <thead>
                    <tr>
                      <th width="10%">Reference</th>
                      <th width="10%">Date</th>
                      <th width="20%">Customer</th>
                      <th width="10%">PO/Ref</th>
                      <th width="10%">Brand</th>
                      <th width="15%">Order Details</th>
                      <th width="10%" className="text-right">
                        Order Cost
                      </th>
                      <th width="10%">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.length === 0 && (
                      <tr>
                        <td colSpan="8" className="text-center">
                          No orders found.
                        </td>
                      </tr>
                    )}

                    {getPaginatedOrders().map((item) => (
                      <tr key={item.orderNumber}>
                        <td>
                          <Link to={`/orders/${item.orderId}`}>
                            {item.orderNumber}
                          </Link>
                        </td>
                        <td>{moment(item.orderDate).format("DD MMM YYYY")}</td>
                        <td>
                          {item.contactCompany?.length > 30
                            ? `${item.contactCompany.substring(0, 20)}...`
                            : item.contactCompany}
                        </td>
                        <td>
                          {item.clientPO?.length > 20
                            ? `${item.clientPO.substring(0, 20)}...`
                            : item.clientPO}
                        </td>
                        <td>{item.brands}</td>
                        <td>{item.orderSummary}</td>
                        <td className="text-right">
                          &pound;
                          {item.orderTotal
                            ? Number.parseFloat(
                              item.orderTotal - (item.orderDiscount ?? 0)
                            ).toLocaleString("en", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })
                            : 0}
                        </td>
                        <td>
                          <span className={`${orderStatusColor[item.orderStatus] || 'unknown-warning'}`}>
                            {statuses[item.orderStatus]?.orderStatusText || 'Unknown'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="paging">
                  <p>Page {page}</p>
                  <button
                    className={page > 1 ? "prev" : "prev disabled"}
                    disabled={page > 1 ? false : true}
                    onClick={prevPage}
                  >
                    <i className="fas fa-angle-left"></i>
                  </button>
                  <button
                    className={page < pageLoaded || (hasNextPage) ? "next" : "next disabled"}
                    disabled={page < pageLoaded || (hasNextPage) ? false : true}
                    onClick={nextPage}
                  >
                    <i className="fas fa-angle-right"></i>
                  </button>
                </div>
              </div>
            )}
          </Container>
        </div>
      </div>
    </div>
  );
}

export default OrdersPage;
